import React, { useEffect, useRef, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { useMarcelMode } from "@/context/MarcelMode.jsx";
import { pointNames } from "@/lib/pointNames.js";

const KML = "Orignal2026_9_points_Eric_NordQuest.kml";
const IOS = "https://apps.apple.com/ca/app/nordquest/id6759458166";
const ANDROID = "https://play.google.com/store/apps/details?id=ca.pulsarinformatique.nordquest";

const LESSONS = [
  {
    id: "ouvrir",
    title: "Ouvrir ma carte et savoir où je suis",
    when: "Avant le départ",
    steps: [
      "Ouvre NordQuest sur ton téléphone.",
      "Ouvre la carte du groupe.",
      "Le point bleu, c’est toi. S’il bouge quand tu marches, c’est bon.",
    ],
  },
  {
    id: "voir",
    title: "Voir un point du groupe",
    when: "Avant le départ",
    steps: [
      "Sur la carte, touche un des 9 points.",
      "Lis son nom.",
      "Tu dois reconnaître les noms d’Éric, pas en inventer.",
    ],
  },
  {
    id: "creer",
    title: "Créer un nouveau point",
    when: "Pendant la chasse",
    steps: [
      "Reste à l’endroit que tu veux garder.",
      "Dans NordQuest, ajoute un point.",
      "Le point doit apparaître sur ta carte.",
    ],
  },
  {
    id: "nommer",
    title: "Bien nommer mon point",
    when: "Pendant la chasse",
    steps: [
      "Donne un nom clair, comme « cache du ruisseau ».",
      "Évite « point 1 » : le groupe ne saura pas ce que c’est.",
      "Vérifie le nom avant de fermer.",
    ],
  },
  {
    id: "modifier",
    title: "Modifier ou supprimer un point",
    when: "Pendant la chasse",
    steps: [
      "Touche le point que tu as créé.",
      "Corrige le nom, ou supprime-le si tu t’es trompé.",
      "Un point du groupe déjà publié ne se change pas sur place.",
    ],
  },
  {
    id: "envoyer",
    title: "Envoyer mes points au chalet",
    when: "Au chalet",
    steps: [
      "Le soir, ouvre NordQuest.",
      "Envoie le fichier de tes nouveaux points.",
      "Reviens ici et dépose-le. Si tu n’as rien ajouté, dis-le.",
    ],
  },
];

const RAIL = [
  ["1", "Installer"],
  ["2", "Carte Sépaq"],
  ["3", "Nos 9 points"],
  ["4", "Apprendre"],
  ["5", "Points du jour"],
  ["6", "Carte du groupe"],
];

const IPHONE_IMPORT = [
  "Télécharger le fichier.",
  "Plus…",
  "Enregistrer dans Fichiers.",
  "Ouvrir NordQuest.",
  "Territoires.",
  "Gérer.",
  "Importer.",
  "Choisir le fichier.",
  "Confirmer que les 9 points sont visibles.",
];

const ANDROID_IMPORT = [
  "Télécharger le fichier.",
  "Ouvrir le dossier Téléchargements.",
  "Le fichier de la carte est là.",
  "Ouvrir NordQuest.",
  "Territoires.",
  "Gérer.",
  "Importer.",
  "Choisir le fichier dans Téléchargements.",
  "Confirmer que les 9 points sont visibles.",
];

function rememberedPhone() {
  try {
    return window.sessionStorage.getItem("orignal2026.nq.phone") || "";
  } catch {
    return "";
  }
}

function nextLabel(mine) {
  if (!mine?.installed) return "Installer NordQuest";
  if (!mine?.sepaq_downloaded) return "Télécharger la carte Sépaq";
  if (!mine?.points_imported) return "Voir nos 9 points";
  if (!mine?.offline_ready) return "Tester hors ligne";
  const done = new Set(mine?.lessons || []);
  if (!done.has("ouvrir") || !done.has("voir")) return "Apprendre à ouvrir la carte";
  return "Téléphone prêt";
}

export function terrainReadyOf(mine) {
  return Boolean(mine?.installed && mine?.sepaq_downloaded && mine?.points_imported && mine?.offline_ready);
}

export function sepaqStatus(mine) {
  if (terrainReadyOf(mine)) return "Prête";
  if (mine?.sepaq_downloaded && mine?.offline_ready) return "Hors ligne testé";
  if (mine?.sepaq_downloaded) return "Téléchargée";
  return "À faire";
}

function recommendedStep(mine) {
  if (!mine?.installed) return 1;
  if (!mine?.sepaq_downloaded || !mine?.offline_ready) return 2;
  if (!mine?.points_imported) return 3;
  return 3;
}

export function nextNordQuestStep(mine) {
  return nextLabel(mine);
}

export function NordQuestCenter() {
  const { actingAsAdmin } = useMarcelMode();
  const admin = actingAsAdmin;
  const [data, setData] = useState(null);
  const [group, setGroup] = useState(null);
  const heldRef = useRef(false);
  const jumped = useRef(false);
  const [step, setStep] = useState(() => {
    const saved = window.sessionStorage.getItem("orignal2026.nq.section");
    window.sessionStorage.removeItem("orignal2026.nq.section");
    heldRef.current = Boolean(saved);
    if (saved === "train") return 4;
    if (saved === "map") return 3;
    if (saved === "points") return 5;
    if (saved === "sepaq") return 2;
    return 1;
  });
  const [phoneKind, setPhoneKind] = useState(rememberedPhone);
  const [guideOn, setGuideOn] = useState(() => {
    try {
      return window.sessionStorage.getItem("orignal2026.nq.import-guide") === "1";
    } catch {
      return false;
    }
  });
  const [fileSaved, setFileSaved] = useState(false);
  const [lessonId, setLessonId] = useState("ouvrir");
  const [dropNote, setDropNote] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [error, setError] = useState("");

  function reload() {
    return Promise.all([
      api("/api/nordquest").then(setData),
      api("/api/nordquest/group").then((body) => {
        if (body && Array.isArray(body.hunters)) setGroup(body);
      }).catch(() => setGroup(null)),
    ]);
  }

  useEffect(() => {
    reload().catch(() => setError("Le centre NordQuest ne répond pas."));
  }, []);

  const hunters = data?.hunters || [];
  const mine = hunters.find((row) => row.member_id === data?.mine) || hunters.find((row) => row.first_name === "Marcel") || null;
  useEffect(() => {
    if (!mine || jumped.current || heldRef.current) return;
    jumped.current = true;
    setStep(recommendedStep(mine));
  }, [mine]);
  const lessons = new Set(mine?.lessons || []);
  const lesson = LESSONS.find((item) => item.id === lessonId) || LESSONS[0];
  const lessonIndex = LESSONS.findIndex((item) => item.id === lesson.id) + 1;
  const readyCount = hunters.filter(terrainReadyOf).length;
  const terrainReady = terrainReadyOf(mine);
  const answeredToday = Boolean(dropNote && dropNote !== "oui");
  const done = {
    1: Boolean(mine?.installed),
    2: Boolean(mine?.sepaq_downloaded && mine?.offline_ready),
    3: Boolean(mine?.points_imported),
    4: lessons.has("ouvrir") && lessons.has("voir"),
    5: answeredToday,
    6: Boolean(mine?.points_imported),
  };
  const finished = [done[1], done[2], done[3], done[4], done[5], done[6]].filter(Boolean).length;
  const versionCode = group?.latest?.code || "1";
  const pointCount = data?.point_count || 9;
  const store = phoneKind === "android" ? ANDROID : IOS;

  async function save(patch) {
    if (!mine) return;
    setError("");
    const saved = await apiJson(`/api/nordquest/status/${mine.member_id}`, patch, "PATCH");
    if (saved?.member_id) {
      setData((current) => ({
        ...current,
        hunters: (current?.hunters || []).map((row) => (row.member_id === saved.member_id ? { ...row, ...saved } : row)),
      }));
    }
  }

  async function markNone() {
    setError("");
    await apiJson("/api/nordquest/group/none", {}, "POST");
    setDropNote("Aucun nouveau point aujourd’hui ✓");
    reload().catch(() => {});
  }

  function choosePhone(kind) {
    setPhoneKind(kind);
    try {
      window.sessionStorage.setItem("orignal2026.nq.phone", kind);
    } catch {
      /* the guide still works for this visit */
    }
  }

  async function downloadMap() {
    if (!phoneKind) {
      setGuideOn(true);
      return;
    }
    setGuideOn(true);
    setError("");
    try {
      window.sessionStorage.setItem("orignal2026.nq.import-guide", "1");
    } catch {
      /* the steps stay on screen anyway */
    }
    try {
      const mapPath = versionCode !== "1" ? `/api/nordquest/group/file/${versionCode}/kml` : `/api/nordquest/file/${KML}`;
      const result = await api(mapPath);
      if (!result?.blob) return;
      const blob = await result.blob();
      const file = new Blob([blob], { type: "application/octet-stream" });
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = versionCode !== "1" ? `Carte-du-groupe-V${versionCode}.kml` : KML;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      setError("Le fichier n’a pas pu être préparé. Les étapes restent ici : réessaie le téléchargement.");
    }
  }

  async function stageFile(file) {
    if (!file) return;
    const names = pointNames(await file.text());
    if (!names.length) {
      setError("Aucun point trouvé dans ce fichier.");
      setPendingFile(null);
      return;
    }
    setError("");
    setPendingFile({ file, names });
  }

  async function deposit() {
    if (!pendingFile?.file) return;
    setError("");
    const body = new FormData();
    body.append("file", pendingFile.file);
    const result = await apiJson("/api/nordquest/group/file", body, "POST");
    const count = result?.point_count ?? pendingFile.names.length;
    setPendingFile(null);
    setDropNote(count ? `${count} nouveaux points trouvés ✓` : "Fichier reçu ✓");
    reload().catch(() => {});
  }

  if (!data) return <p className="og-hint">{error || "Préparation du téléphone…"}</p>;

  return (
    <div className="og-nqcenter" data-testid="nq-center">
      <p className="og-nqcenter__who" data-testid="nq-progress">
        {mine?.first_name} — {finished}/6 étapes
        {" · "}Prochaine étape : {nextLabel(mine)}
      </p>
      <ol className="og-nqrail" data-testid="nq-rail">
        {RAIL.map(([num, label], index) => (
          <li key={num}>
            <button type="button" className={step === index + 1 ? "is-on" : done[index + 1] ? "is-done" : ""} onClick={() => { jumped.current = true; setStep(index + 1); }}>
              {num} {label}{done[index + 1] ? " ✓" : ""}
            </button>
          </li>
        ))}
      </ol>
      {terrainReady ? (
        <p className="og-nqready" data-testid="nq-terrain-ready">PRÊT POUR LE TERRAIN ✓</p>
      ) : (
        <p className="og-nqwait" data-testid="nq-terrain-incomplete">PRÉPARATION INCOMPLÈTE · {nextLabel(mine)}</p>
      )}

      {step === 1 ? (
        <section data-testid="nq-step-install">
          <h3>Quel téléphone as-tu ?</h3>
          <div className="og-nqcenter__actions">
            <button type="button" className={`og-btn${phoneKind === "iphone" ? " og-btn--primary" : ""}`} data-testid="nq-iphone" onClick={() => choosePhone("iphone")}>iPhone</button>
            <button type="button" className={`og-btn${phoneKind === "android" ? " og-btn--primary" : ""}`} data-testid="nq-android" onClick={() => choosePhone("android")}>Android</button>
          </div>
          {phoneKind ? (
            <p><a className="og-btn og-btn--primary" data-testid="nq-install-link" href={store} target="_blank" rel="noreferrer">Installer NordQuest</a></p>
          ) : null}
          <button type="button" className="og-btn og-btn--primary" data-testid="nq-installed-ok" onClick={() => save({ installed: true }).then(() => setStep(2))}>
            J’ai installé NordQuest ✓
          </button>
        </section>
      ) : null}

      {step === 2 ? (
        <section data-testid="nq-step-sepaq">
          <h3>Carte officielle Sépaq — Portneuf</h3>
          <p data-testid="nq-sepaq-status">{sepaqStatus(mine)}</p>
          <p>Cette carte se télécharge dans NordQuest. Orignal2026 ne la copie pas.</p>
          <ol data-testid="nq-sepaq-guide">
            <li>Ouvrir NordQuest.</li>
            <li>Aller dans Cartes publiques.</li>
            <li>Choisir Sépaq.</li>
            <li>Choisir Réserve faunique de Portneuf.</li>
            <li>Télécharger la carte.</li>
            <li>Vérifier que les chemins, lacs, reliefs et détails apparaissent.</li>
            <li>Revenir dans Orignal2026.</li>
          </ol>
          <button type="button" className="og-btn og-btn--primary" data-testid="nq-sepaq-ok" onClick={() => save({ sepaq_downloaded: true })}>
            Ma carte Sépaq est téléchargée
          </button>
          {mine?.sepaq_downloaded ? (
            <div data-testid="nq-offline">
              <h3>Tester hors ligne</h3>
              <ol>
                <li>Ouvrir NordQuest.</li>
                <li>Vérifier que la carte Portneuf est visible.</li>
                <li>Activer le mode avion.</li>
                <li>Vérifier que la carte reste visible.</li>
                <li>Vérifier que les 9 points du groupe sont encore accessibles.</li>
                <li>Revenir dans Orignal2026.</li>
              </ol>
              <button type="button" className="og-btn og-btn--primary" data-testid="nq-offline-ok" onClick={() => save({ offline_ready: true })}>
                Ça fonctionne hors ligne
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {step === 3 ? (
        <section data-testid="nq-step-map">
          <h3>Installer la carte du groupe</h3>
          <p data-testid="nq-map-now">Carte actuelle : V{versionCode} · {pointCount} points</p>
          <h3>Quel téléphone as-tu ?</h3>
          <div className="og-nqcenter__actions">
            <button type="button" className={`og-btn${phoneKind === "iphone" ? " og-btn--primary" : ""}`} data-testid="nq-map-iphone" onClick={() => choosePhone("iphone")}>iPhone</button>
            <button type="button" className={`og-btn${phoneKind === "android" ? " og-btn--primary" : ""}`} data-testid="nq-map-android" onClick={() => choosePhone("android")}>Android</button>
          </div>
          <button type="button" className="og-btn og-btn--primary" data-testid="nq-download-map" onClick={downloadMap}>
            Télécharger notre carte
          </button>
          {!phoneKind && guideOn ? <p>Choisis iPhone ou Android pour voir les bonnes étapes.</p> : null}
          {guideOn ? (
            <div data-testid="nq-import-guide">
              <p className="og-nqwarn">
                {phoneKind === "android"
                  ? "Si le fichier s’ouvre tout seul, reviens ici. Il est aussi dans Téléchargements."
                  : "Si l’aperçu du fichier s’ouvre, ne reste pas là. Touche Plus… puis Enregistrer dans Fichiers."}
              </p>
              <ol className="og-nqguide">
                {(phoneKind === "android" ? ANDROID_IMPORT : IPHONE_IMPORT).map((line, index) => (
                  <li key={line} className={index === 0 ? "is-done" : ""}>{line}</li>
                ))}
              </ol>
              {!fileSaved ? (
                <button type="button" className="og-btn og-btn--primary" data-testid="nq-file-saved" onClick={() => setFileSaved(true)}>
                  J’ai enregistré le fichier
                </button>
              ) : (
                <a className="og-btn og-btn--primary" data-testid="nq-open-app" href={phoneKind === "android" ? ANDROID : IOS} target="_blank" rel="noreferrer">
                  Ouvrir NordQuest
                </a>
              )}
            </div>
          ) : (
            <p>Après le téléchargement, les étapes s’affichent ici. Ne reste pas dans l’aperçu du fichier.</p>
          )}
          {guideOn && fileSaved ? (
            <button type="button" className="og-btn og-btn--primary" data-testid="nq-points-ok" onClick={() => save({ points_imported: true, map_opened: true })}>
              Je vois les 9 points ✓
            </button>
          ) : null}
        </section>
      ) : null}

      {step === 4 ? (
        <section data-testid="nq-step-learn">
          <h3>Apprendre NordQuest</h3>
          <p data-testid="nq-lesson-count">Formation : {lessonIndex} / 6 · {lessons.size}/6 comprises</p>
          <div className="og-nqlessons">
            {LESSONS.map((item) => (
              <button key={item.id} type="button" className={lessonId === item.id ? "is-on" : ""} data-testid={`nq-lesson-${item.id}`} onClick={() => setLessonId(item.id)}>
                {lessons.has(item.id) ? "✓ " : ""}{item.title}
              </button>
            ))}
          </div>
          <article data-testid="nq-lesson">
            <p>{lesson.when}</p>
            <h3>{lesson.title}</h3>
            <p className="og-nqpic" aria-hidden="true">{lessonIndex <= 2 ? "🗺️" : lessonIndex === 6 ? "📤" : "📍"}</p>
            <ol>
              {lesson.steps.map((line) => <li key={line}>{line}</li>)}
            </ol>
            <button type="button" className="og-btn og-btn--primary" data-testid="nq-lesson-ok" onClick={() => save({ lesson: lesson.id })}>J’ai compris ✓</button>
            <p><a className="og-btn" href={phoneKind === "android" ? ANDROID : IOS} target="_blank" rel="noreferrer">Essayer maintenant dans NordQuest</a></p>
          </article>
        </section>
      ) : null}

      {step === 5 ? (
        <section data-testid="nq-step-day">
          <h3>AS-TU CRÉÉ DES NOUVEAUX POINTS AUJOURD’HUI ?</h3>
          <div className="og-nqcenter__actions">
            <button type="button" className="og-btn og-btn--primary" data-testid="nq-points-yes" onClick={() => setDropNote("oui")}>Oui — envoyer mes nouveaux points</button>
            <button type="button" className="og-btn" data-testid="nq-points-no" onClick={markNone}>Non — aucun nouveau point</button>
          </div>
          {dropNote === "oui" ? (
            <>
              <ol>
                <li>Dans NordQuest : préparer mes nouveaux points.</li>
                <li>Revenir ici : choisir mon fichier.</li>
                <li>Envoyer mes points au groupe.</li>
              </ol>
              <details>
                <summary>Options avancées</summary>
                <p>Le partage NordQuest peut être un fichier KML ou GPX.</p>
              </details>
            </>
          ) : null}
          {dropNote === "oui" ? (
            <label className="og-btn og-btn--primary" data-testid="nq-deposit">
              Choisir mon fichier
              <input type="file" accept=".kml,.gpx,.xml" hidden onChange={(event) => stageFile(event.target.files?.[0])} />
            </label>
          ) : null}
          {pendingFile ? (
            <div data-testid="nq-found">
              <p>{pendingFile.names.length} nouveaux points trouvés ✓</p>
              <ul>{pendingFile.names.map((name) => <li key={name}>{name}</li>)}</ul>
              <button type="button" className="og-btn og-btn--primary" data-testid="nq-send-points" onClick={deposit}>
                Envoyer mes {pendingFile.names.length} points au groupe
              </button>
            </div>
          ) : null}
          {dropNote && dropNote !== "oui" ? <p data-testid="nq-drop-note">{dropNote}</p> : null}
          <h3>Points du jour</h3>
          <ul data-testid="nq-evening">
            {(group?.hunters || hunters).map((row) => {
              const label = row.status === "received" ? "reçu ✓" : row.status === "none" ? "aucun nouveau point ✓" : row.label === "Pas de fichier aujourd’hui" ? "aucun nouveau point ✓" : row.label === "Fichier reçu ✅" ? "reçu ✓" : "en attente";
              return <li key={row.member_id}>{row.first_name} — {group ? label : "en attente"}</li>;
            })}
          </ul>
        </section>
      ) : null}

      {step === 6 ? (
        <section data-testid="nq-step-group">
          <h3>Carte du groupe</h3>
          <p>Carte actuelle : V{versionCode} · {pointCount} points · 29 août 2026 pour la V1.</p>
          <p>V1 reste la carte d’origine. Une nouvelle carte, plus tard, s’appellera V2. On ne change jamais V1.</p>
          {versionCode !== "1" ? (
            <p>
              <a className="og-btn og-btn--primary" href={`/api/nordquest/group/file/${versionCode}/kml`}>Mettre ma carte à jour</a>
            </p>
          ) : <p>Aucune nouvelle carte pour le moment.</p>}
          <button type="button" className="og-btn" data-testid="nq-installed-version" onClick={() => apiJson("/api/nordquest/group/installed", { code: versionCode }, "POST")}>
            Je vois les nouveaux points ✓
          </button>
          <h3>Les 9 points</h3>
          <ul data-testid="nq-point-names">
            {(data.points || []).map((point) => <li key={point.id || point.name}>{point.name}</li>)}
          </ul>
          {admin ? (
            <section data-testid="nq-admin">
              <h3>Préparer la carte du groupe</h3>
              <p data-testid="nq-preview">
                {group?.preview ? `${group.preview.new_count || 0} nouveaux points · ${group.preview.duplicates?.length || 0} doublons · ${group.preview.unresolved || 0} point à vérifier` : "Aucun fichier reçu pour une nouvelle carte."}
              </p>
              <ul data-testid="nq-new-points">
                {(group?.preview?.new_points || []).map((point) => (
                  <li key={point.key || point.name}>{point.name} — {point.author || "auteur à confirmer"}</li>
                ))}
              </ul>
              <p>Un doublon, c’est le même nom à 20 m ou moins. Sinon, c’est à vérifier. Rien n’est fusionné tout seul.</p>
              {group?.preview?.unresolved ? <p>Il reste un point à vérifier avant de publier.</p> : null}
              {group?.preview?.new_count && !group.preview.unresolved ? (
                <button type="button" className="og-btn og-btn--primary" data-testid="nq-publish" onClick={() => apiJson("/api/nordquest/group/publish", {}, "POST").then(() => reload())}>
                  Publier la nouvelle carte
                </button>
              ) : <p>Publier la nouvelle carte sera possible quand il y aura de nouveaux points, et plus rien à vérifier.</p>}
              <p>Exemple, pas encore publié : V2 · 4 octobre · 14 points. V1 du 29 août resterait intacte.</p>
              <table data-testid="nq-admin-table">
                <thead>
                  <tr><th>Chasseur</th><th>NordQuest</th><th>Carte Sépaq</th><th>9 points</th><th>Hors ligne</th><th>Prêt</th></tr>
                </thead>
                <tbody>
                  {hunters.map((row) => (
                    <tr key={row.member_id} data-testid={`nq-admin-${row.member_id}`}>
                      <td>{row.first_name}</td>
                      <td>{row.installed ? "✓" : "—"}</td>
                      <td>{row.sepaq_downloaded ? "✓" : "—"}</td>
                      <td>{row.points_imported ? "✓" : "—"}</td>
                      <td>{row.offline_ready ? "✓" : "—"}</td>
                      <td>{terrainReadyOf(row) ? "✓" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <details>
                <summary>⋯ Corriger un statut</summary>
                {hunters.map((row) => (
                  <button key={row.member_id} type="button" className="og-btn" onClick={() => apiJson(`/api/nordquest/status/${row.member_id}`, { installed: false, map_opened: false, sepaq_downloaded: false, points_imported: false, offline_ready: false, lessons: [] }, "PATCH").then(reload)}>
                    Recommencer {row.first_name}
                  </button>
                ))}
              </details>
            </section>
          ) : null}
        </section>
      ) : null}

      <p className="og-nqfoot" data-testid="nq-group-count">{readyCount} / 6 chasseurs prêts</p>
      {error ? <p className="og-error">{error}</p> : null}
    </div>
  );
}
